import {
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import type { AgentFieldDef } from "@speech-agent/common";

import { useTRPC } from "@/infrastructure/trpc/react";
import { LiveConversation } from "@/speech-agent/components/live-conversation";

export const Route = createFileRoute("/app/speech-agent/")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(
      context.trpc.speechAgent.agents.listPublic.queryOptions()
    );
    void context.queryClient.prefetchQuery(
      context.trpc.speechAgent.config.get.queryOptions()
    );
  },
  component: SpeechAgentHome,
});

type Phase =
  | { kind: "setup" }
  | {
      kind: "live";
      sessionId: string;
      agentId: string;
      fields: ReadonlyArray<AgentFieldDef>;
    }
  | {
      kind: "finalizing";
      sessionId: string;
      agentId: string;
    }
  | {
      kind: "result";
      reportMd: string;
      transcript: string;
    };

function SpeechAgentHome() {
  const trpc = useTRPC();
  const [phase, setPhase] = useState<Phase>({
    kind: "setup",
  });
  const [selectedAgentId, setSelectedAgentId] =
    useState<string>("");

  const { data: agents } = useSuspenseQuery(
    trpc.speechAgent.agents.listPublic.queryOptions()
  );

  const seedDefaults = useMutation(
    trpc.speechAgent.agents.seedDefaults.mutationOptions({
      onSuccess: () => {
        window.location.reload();
      },
    })
  );

  const startSession = useMutation(
    trpc.speechAgent.session.start.mutationOptions()
  );

  const finalizeSession = useMutation(
    trpc.speechAgent.session.finalize.mutationOptions()
  );

  useEffect(() => {
    if (
      selectedAgentId === "" &&
      agents.length > 0 &&
      agents[0]
    ) {
      setSelectedAgentId(agents[0].id);
    }
  }, [agents, selectedAgentId]);

  const handleStart = useCallback(async () => {
    if (!selectedAgentId) return;
    const result = await startSession.mutateAsync({
      agentId: selectedAgentId,
    });
    setPhase({
      kind: "live",
      sessionId: result.sessionId,
      agentId: result.agentId,
      fields: result.fields,
    });
  }, [selectedAgentId, startSession]);

  const handleFinalize = useCallback(async () => {
    if (
      phase.kind !== "live" &&
      phase.kind !== "finalizing"
    )
      return;
    const sessionId = phase.sessionId;
    const agentId = phase.agentId;
    setPhase({ kind: "finalizing", sessionId, agentId });
    const result = await finalizeSession.mutateAsync({
      sessionId,
      agentId,
    });
    setPhase({
      kind: "result",
      reportMd: result.reportMd,
      transcript: result.transcript,
    });
  }, [phase, finalizeSession]);

  const downloadReport = useCallback((reportMd: string) => {
    const blob = new Blob([reportMd], {
      type: "text/markdown",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zapis-${new Date()
      .toISOString()
      .slice(0, 16)
      .replace(/[:T]/g, "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const downloadPdf = useCallback(
    async (reportMd: string) => {
      const res = await fetch("/api/session/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportMd }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "zapis.pdf";
      a.click();
      URL.revokeObjectURL(url);
    },
    []
  );

  const copyReport = useCallback((reportMd: string) => {
    void navigator.clipboard.writeText(reportMd);
  }, []);

  if (agents.length === 0) {
    return (
      <section className="page" id="page-home">
        <section className="hero">
          <p className="kicker">— 01 / Vítej</p>
          <h1 className="display">
            Žádní agenti,
            <br />
            <em>zatím</em>.
          </h1>
          <p className="lede">
            Pojďme tě nakopnout výchozím agentem pro zápis z
            porady.
          </p>
          <div className="setup-form">
            <button
              type="button"
              className="primary-btn primary-btn-lg"
              onClick={() => seedDefaults.mutate()}
              disabled={seedDefaults.isPending}
            >
              {seedDefaults.isPending
                ? "Sázím…"
                : "Nasadit výchozí agenty"}
              <span className="arrow">→</span>
            </button>
          </div>
        </section>
      </section>
    );
  }

  return (
    <section className="page" id="page-home">
      {phase.kind === "setup" && (
        <section className="hero">
          <p className="kicker">— 01 / Nová konverzace</p>
          <h1 className="display">
            S kým si dnes
            <br />
            <em>promluvíš</em>?
          </h1>
          <p className="lede">
            Vyber agenta a začni mluvit. Agent klade otázky,
            ty odpovídáš volně — z přepisu vznikne
            strukturovaný zápis.
          </p>

          <div className="setup-form">
            <label
              className="field-label"
              htmlFor="agent-select"
            >
              Agent
            </label>
            <div className="select-wrap">
              <select
                id="agent-select"
                value={selectedAgentId}
                onChange={(e) =>
                  setSelectedAgentId(e.target.value)
                }
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              <span
                className="select-arrow"
                aria-hidden="true"
              >
                ↓
              </span>
            </div>

            <button
              type="button"
              className="primary-btn primary-btn-lg"
              onClick={() => void handleStart()}
              disabled={
                !selectedAgentId || startSession.isPending
              }
            >
              {startSession.isPending
                ? "Připojuji…"
                : "Spustit konverzaci"}
              <span className="arrow">→</span>
            </button>
            {startSession.error && (
              <p className="status error">
                {startSession.error.message}
              </p>
            )}
          </div>

          <div className="step-row" aria-hidden="true">
            <span className="step step-active">
              <span className="step-num">01</span>
              <span className="step-label">Mluvíš</span>
            </span>
            <span className="step-rule" />
            <span className="step">
              <span className="step-num">02</span>
              <span className="step-label">
                Strukturuje
              </span>
            </span>
            <span className="step-rule" />
            <span className="step">
              <span className="step-num">03</span>
              <span className="step-label">Stáhneš</span>
            </span>
          </div>
        </section>
      )}

      {(phase.kind === "live" ||
        phase.kind === "finalizing") && (
        <LiveConversation
          sessionId={phase.sessionId}
          agentId={phase.agentId}
          fields={phase.kind === "live" ? phase.fields : []}
          onFinalize={() => void handleFinalize()}
          finalizing={phase.kind === "finalizing"}
        />
      )}

      {phase.kind === "result" && (
        <section className="card result-card">
          <div className="card-head">
            <p className="kicker">— 03 / Hotovo</p>
            <h2 className="card-title">Tvůj zápis</h2>
          </div>
          <pre className="report">{phase.reportMd}</pre>
          <details className="raw-details">
            <summary>
              Přepis konverzace (pro kontrolu)
            </summary>
            <pre className="raw">{phase.transcript}</pre>
          </details>
          <div className="card-actions card-actions-bottom">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => copyReport(phase.reportMd)}
            >
              Kopírovat MD
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => downloadReport(phase.reportMd)}
            >
              Stáhnout .md
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() =>
                void downloadPdf(phase.reportMd)
              }
            >
              Stáhnout PDF
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setPhase({ kind: "setup" })}
            >
              Nová konverzace
            </button>
          </div>
        </section>
      )}
    </section>
  );
}
